import { FireIcon, FaceFrownIcon, SignalSlashIcon } from '@heroicons/react/24/outline'
import { Heading } from '@/components/base/heading'
import { Strong, Text, TextLink } from '@/components/base/text'
import { useRouteError } from 'react-router'
import { Button } from './base/button'
import { CombinedGraphQLErrors, type ErrorLike } from '@apollo/client'

interface Props {
  message: string
  code?: string
}

interface ApiProps {
  error: ErrorLike | undefined
}

export const FullPage = ({ message, code }: Props) => {
  return (
    <div className="flex flex-row grow justify-center items-center">
      <div
        className={ `flex flex-col gap-12 items-center p-16 bg-white shadow-lg ring-1 ring-zinc-950/10 dark:bg-zinc-900
                    dark:ring-white/10 forced-colors:outline rounded-3xl shrink` }
      >
        <Text><FireIcon className="w-24 h-24" /></Text>
        <div className="flex flex-col items-center sm:max-w-lg">
          <Heading className="pb-6" size="2xl">{ message }</Heading>
          <Text className="text-center text-balance">Stop the show! That wasn't meant to happen.</Text>
          <Text className="text-center text-balance">
            Give it another go, and if it happens again, drop us a line at&nbsp;
            <TextLink href="mailto:support@mystro.uk">support@mystro.uk</TextLink>,
            we'll get you going again
          </Text>
          { code && <Text className="pt-6">Error code: <Strong>{ code }</Strong></Text> }
        </div>
      </div>
    </div>
  )
}

export const Inline = ({ message, code }: Props) => {
  return (
    <div className="h-full w-full flex justify-center">
      <div
        className={ `flex flex-row gap-12 items-center py-8 px-16 bg-white shadow-lg ring-1 ring-zinc-950/10
                    dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline rounded-3xl` }
      >
        <Text><FaceFrownIcon className="w-24 h-24" /></Text>
        <div className="flex flex-col items-center sm:max-w-lg">
          <Heading className="pb-6" size="2xl">{ message }</Heading>
          <Text className="text-center text-balance">Well, this isn't right.</Text>
          <Text className="text-center text-balance">
            Give it another go, and if it happens again, drop us a line at&nbsp;
            <TextLink href="mailto:support@mystro.uk">support@mystro.uk</TextLink>,
            we'll get you going again
          </Text>
          { code && <Text className="pt-6">Error code: <Strong>{ code }</Strong></Text> }
        </div>
      </div>
    </div>
  )
}

export const Slimline = ({ error }: ApiProps) => {
  const message = CombinedGraphQLErrors.is(error) ? [...new Set(error.errors.map(e => e.message))].join(', ') : error?.message

  return (
    <div className="py-6 w-full flex justify-center">
      <div
        className={ `flex flex-row gap-12 items-center py-6 px-12 bg-white shadow-lg ring-1 ring-zinc-950/10
                    dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline rounded-3xl` }
      >
        <Text><FaceFrownIcon className="w-20 h-20" /></Text>
        <div className="flex flex-col items-center">
          <Heading className="pb-6" size="2xl">{ message }</Heading>
          <Text className="text-center text-balance">
            Well, this isn't right. Give it another go, and if it happens again, drop us a line at&nbsp;
            <TextLink href="mailto:support@mystro.uk">support@mystro.uk</TextLink>,
            we'll get you going again
          </Text>
        </div>
      </div>
    </div>
  )
}

interface RouterError extends Error { statusText?: string }

export const NotFound = () => {
  const error = useRouteError() as RouterError
  console.error(error)

  return (
    <div className="flex flex-row grow justify-center items-center">
      <div
        className={ `flex flex-col gap-12 items-center p-16 bg-white shadow-lg ring-1 ring-zinc-950/10
                    dark:bg-zinc-900 dark:ring-white/10 forced-colors:outline rounded-3xl shrink` }
      >
        <Text><SignalSlashIcon className="w-24 h-24" /></Text>
        <div className="flex flex-col items-center sm:max-w-lg">
          <Heading className="pb-6" size="2xl">{ error.statusText || 'That\'s not quite right' }</Heading>
          <Text className="text-center text-balance">Just when you need them, they're nowhere to be found.</Text>
          <Text className="text-center text-balance">
            Give it another go, and if it happens again, drop us a line at&nbsp;
            <TextLink href="mailto:support@mystro.uk">support@mystro.uk</TextLink>,
            we'll find what you're looking for
          </Text>
          <Text className="pt-6 text-center wrap-break-word">
            <Strong className="wrap-break-word">{ error.message }</Strong>
          </Text>
          <span className="pt-6"><Button color="zinc" href="/">Reload</Button></span>
        </div>
      </div>
    </div>
  )
}
